<template>
  <v-dialog
    :model-value="addressDialog"
    persistent
    width="min(760px, 90vw)"
    class="py-5"
    @update:model-value="(value) => emit('update:addressDialog', value)"
  >
    <v-card>
      <v-col>
        <v-card height="100%" :flat="true" border class="mx-auto">
          <v-card-title class="justify-center mb-4">
            {{ "住所選択" }}
          </v-card-title>
          <v-card-text>
            <v-row class="justify-center">
              <v-btn-toggle
                :model-value="toggleMode"
                rounded
                density="compact"
                color="#2196F3"
                class="pt-2"
                selected-class="bg-primary text-white border-primary"
              >
                <v-btn
                  :value="0"
                  class="pl-10 pr-10"
                  variant="outlined"
                  color="grey-lighten-1"
                  @click="emit('toggleZip')"
                >
                  {{ wordList.lbl_zipcode || "郵便番号" }}
                </v-btn>
                <v-btn
                  :value="1"
                  class="pl-10 pr-10"
                  variant="outlined"
                  color="grey-lighten-1"
                  @click="emit('togglePref')"
                >
                  {{ wordList.lbl_prefectures || "都道府県" }}
                </v-btn>
              </v-btn-toggle>
            </v-row>

            <v-row v-if="toggleMode === 0">
              <v-col align-self="center">
                <v-text-field
                  :model-value="zipCode"
                  class="address-input"
                  :placeholder="wordList.plc_zipcode || ''"
                  density="compact"
                  variant="outlined"
                  :hint="wordList.lbl_no_hyphen || '※ハイフンなしで入力'"
                  :error-messages="zipFieldErrorMessages"
                  persistent-hint
                  @update:model-value="onZipModelUpdate"
                  @blur="onZipBlur"
                />
              </v-col>
            </v-row>

            <v-row v-if="toggleMode === 1">
              <v-col align-self="center">
                <v-autocomplete
                  :model-value="pref"
                  class="address-input"
                  density="compact"
                  :items="normalizedPrefListItems"
                  :placeholder="wordList.plc_selectbox || ''"
                  :no-data-text="wordList.lbl_no_data_text || ''"
                  bg-color="white"
                  variant="outlined"
                  :error-messages="prefFieldErrorMessages"
                  @update:model-value="onPrefModelUpdate"
                  @blur="onPrefBlur"
                />
              </v-col>
            </v-row>
            <v-row v-if="showExternalPrefError" dense>
              <v-col offset="1">
                <v-card-text class="text-red px-0 py-0">{{ errors.pref }}</v-card-text>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="justify-center flex-wrap">
            <v-btn variant="flat" color="return" class="btn_width_1 my-2 mx-2" @click="emit('closeAddress')">
              <v-icon>mdi-arrow-left</v-icon>
              {{ wordList.btn_return || "戻る" }}
            </v-btn>
            <v-btn variant="flat" color="primary" class="btn_width_1 my-2 mx-2" :disabled="clickDisabled" @click="onSearchClick">
              <v-icon>mdi-playlist-edit</v-icon>
              {{ wordList.btn_search || "検索" }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-card>
  </v-dialog>

  <v-dialog
    :model-value="areaDialog"
    persistent
    width="min(760px, 90vw)"
    class="py-5"
    @update:model-value="(value) => emit('update:areaDialog', value)"
  >
    <v-card>
      <v-card-title>{{ wordList.lbl_area_select || "エリア選択" }}</v-card-title>
      <v-card-text>
        <v-divider />
        <v-row class="font-weight-black text-h6 py-2">
          <v-col>
            {{ wordList.lbl_selected_address || "選択住所" }} ： {{ selectedAddress }}
          </v-col>
        </v-row>
        <v-divider />

        <v-row v-if="cityBlockList && !sectionBlockList && originalCity.length === 0" dense>
          <v-col class="py-2 pl-0">
            <template v-for="group in kanaGroups" :key="group.key">
              <v-card v-if="getKanaItems(cityBlockList, group.key).length > 0" :flat="true">
                <v-card-text class="pb-0">{{ group.label }}</v-card-text>
                <v-btn
                  v-for="value in getKanaItems(cityBlockList, group.key)"
                  :key="`city-${group.key}-${value}`"
                  class="my-2 mx-2"
                  color="primary"
                  variant="outlined"
                  min-width="200"
                  :disabled="clickDisabled"
                  @click="emit('selectCity', value)"
                >
                  {{ value }}
                </v-btn>
              </v-card>
            </template>
          </v-col>
        </v-row>

        <v-row v-if="sectionBlockList && blockList.length === 0" dense>
          <v-col class="py-2 pl-0">
            <template v-for="group in kanaGroups" :key="group.key">
              <v-card v-if="getKanaItems(sectionBlockList, group.key).length > 0" :flat="true">
                <v-card-text class="pb-0">{{ group.label }}</v-card-text>
                <v-btn
                  v-for="value in getKanaItems(sectionBlockList, group.key)"
                  :key="`section-${group.key}-${value}`"
                  class="mx-3 my-2"
                  color="primary"
                  variant="outlined"
                  min-width="200"
                  :disabled="clickDisabled"
                  @click="emit('selectSection', value)"
                >
                  {{ value }}
                </v-btn>
              </v-card>
            </template>
          </v-col>
        </v-row>

        <v-row v-if="showBlockGuideMessage" class="mt-0">
          <v-col class="py-2">
            <span v-if="areaNotSelectMessage" class="area-not-select-message" v-html="areaNotSelectMessage" />
            <br v-if="areaNotSelectMessage && showOtherBlockGuideMessage" />
            <span v-if="showOtherBlockGuideMessage" class="area-not-select-message">
              {{ wordList.lbl_other_block_select || "" }}
            </span>
          </v-col>
        </v-row>

        <v-row v-if="normalBlockItems.length > 0" dense>
          <v-col class="py-2">
            <v-card :flat="true">
              <v-btn
                v-for="block in normalBlockItems"
                :key="`block-${String(block.name)}`"
                class="mx-3 my-2"
                color="primary"
                variant="outlined"
                min-width="200"
                :disabled="clickDisabled || !block.enabled"
                @click="emit('selectBlock', String(block.name ?? ''))"
              >
                {{ block.name ?? "" }}
              </v-btn>
            </v-card>
          </v-col>
        </v-row>

        <v-row v-if="enabledOtherBlockItems.length > 0" dense>
          <v-col class="py-2">
            <v-card :flat="true">
              <v-btn
                v-for="block in enabledOtherBlockItems"
                :key="`other-block-${String(block.name)}`"
                class="mx-3 my-2"
                color="primary"
                variant="outlined"
                min-width="200"
                :disabled="clickDisabled || !block.enabled"
                @click="emit('selectBlock', String(block.name ?? ''))"
              >
                {{ block.name ?? "" }}
              </v-btn>
            </v-card>
          </v-col>
        </v-row>

        <v-row v-if="errors.general" dense>
          <v-col>
            <v-card-text class="text-red px-0 py-0">{{ errors.general }}</v-card-text>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="justify-center">
        <v-btn variant="flat" color="return" class="btn_width_1 my-2 mx-2" @click="emit('areaReturn')">
          <v-icon>mdi-arrow-left</v-icon>
          {{ wordList.btn_return || "戻る" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { useField } from "vee-validate";
import { computed, ref, watch } from "vue";
import type { AddressBlockItem, AddressSelectionErrors, KanaStepA } from "./types";

/** カナ行キーを表す型。 */
type KanaGroupKey =
  | "line_a"
  | "line_ka"
  | "line_sa"
  | "line_ta"
  | "line_na"
  | "line_ha"
  | "line_ma"
  | "line_ya"
  | "line_ra"
  | "line_wa"
  | "line_other";

/** コンポーネント入力を表す型。 */
type AddressSelectorDialogProps = {
  addressDialog: boolean;
  areaDialog: boolean;
  toggleMode: number;
  zipCode: string;
  pref: string;
  prefListItems: Array<string | null | undefined>;
  selectedAddress: string;
  originalCity: string;
  cityBlockList: KanaStepA | null;
  sectionBlockList: KanaStepA | null;
  blockList: AddressBlockItem[];
  clickDisabled: boolean;
  areaNotSelectMessage?: string;
  errors: AddressSelectionErrors;
  wordList: Record<string, string>;
};

/** コンポーネント出力イベントを表す型。 */
type AddressSelectorDialogEmits = {
  (e: "update:addressDialog", value: boolean): void;
  (e: "update:areaDialog", value: boolean): void;
  (e: "update:zipCode", value: string): void;
  (e: "update:pref", value: string): void;
  (e: "toggleZip"): void;
  (e: "togglePref"): void;
  (e: "zipInput"): void;
  (e: "prefInput"): void;
  (e: "search"): void;
  (e: "closeAddress"): void;
  (e: "areaReturn"): void;
  (e: "selectCity", value: string): void;
  (e: "selectSection", value: string): void;
  (e: "selectBlock", value: string): void;
};

const props = defineProps<AddressSelectorDialogProps>();
const emit = defineEmits<AddressSelectorDialogEmits>();
const persistentZipExternalError = ref<string>("");

const zipValidationRule = (value: string | undefined): true | string => {
  const normalized = String(value ?? "");
  if (!normalized) {
    return "郵便番号が入力されていません。";
  }
  if (!/^[0-9]+$/.test(normalized)) {
    return "郵便番号は数字のみ使用できます";
  }
  if (normalized.length !== 7) {
    return "郵便番号は7文字でなければなりません。";
  }
  return true;
};

const prefValidationRule = (value: string | undefined): true | string => {
  if (!String(value ?? "").trim()) {
    return "都道府県が入力されていません。";
  }
  return true;
};

const {
  errorMessage: zipFieldErrorMessage,
  validate: validateZipField,
  setValue: setZipFieldValue,
  resetField: resetZipField,
  setErrors: setZipFieldErrors,
} = useField<string>("address_selector_zip_code", zipValidationRule, {
  initialValue: props.zipCode,
  validateOnValueUpdate: false,
  standalone: true,
});

const {
  errorMessage: prefFieldErrorMessage,
  validate: validatePrefField,
  setValue: setPrefFieldValue,
  resetField: resetPrefField,
  setErrors: setPrefFieldErrors,
} = useField<string>("address_selector_prefecture", prefValidationRule, {
  initialValue: props.pref,
  validateOnValueUpdate: false,
  standalone: true,
});

watch(
  () => props.zipCode,
  (value) => {
    setZipFieldValue(String(value ?? ""), false);
  }
);

watch(
  () => props.pref,
  (value) => {
    setPrefFieldValue(String(value ?? ""), false);
  }
);

watch(
  () => props.errors.pref,
  (value) => {
    if (props.toggleMode !== 1) {
      return;
    }
    if (value) {
      setPrefFieldErrors([value]);
      return;
    }
    setPrefFieldErrors([]);
  }
);

watch(
  () => props.toggleMode,
  (value) => {
    if (value !== 0) {
      resetZipField({ value: props.zipCode });
    }
    if (value !== 1) {
      resetPrefField({ value: props.pref });
    }
  }
);

watch(
  () => props.errors.zip,
  (value) => {
    // 画面側で判定した「配達対象外」エラーも Field エラーへ寄せて表示を一本化する。
    if (props.toggleMode !== 0) {
      return;
    }
    if (value) {
      persistentZipExternalError.value = value;
      setZipFieldErrors([value]);
      return;
    }
    if (!persistentZipExternalError.value) {
      setZipFieldErrors([]);
    }
  }
);

const zipFieldErrorMessages = computed(() => {
  return zipFieldErrorMessage.value ? [zipFieldErrorMessage.value] : [];
});

const prefFieldErrorMessages = computed(() => {
  return prefFieldErrorMessage.value ? [prefFieldErrorMessage.value] : [];
});

const showExternalPrefError = computed(() => {
  return props.toggleMode === 1 && !!props.errors.pref && !prefFieldErrorMessage.value;
});

const onZipModelUpdate = (value: unknown): void => {
  const normalized = String(value ?? "");
  // 入力変更時は「対象外」判定をリセットし、再検索で再判定する。
  persistentZipExternalError.value = "";
  setZipFieldErrors([]);
  setZipFieldValue(normalized, false);
  emit("update:zipCode", normalized);
};

const onZipBlur = async (): Promise<void> => {
  await validateZipField();
  emit("zipInput");
};

const onSearchClick = async (): Promise<void> => {
  const currentMode = Number(props.toggleMode);

  if (currentMode === 0) {
    if (persistentZipExternalError.value) {
      setZipFieldErrors([persistentZipExternalError.value]);
      return;
    }

    // 検索ボタン押下時は毎回ルール検証を実行する。
    const result = await validateZipField();
    if (!result.valid) {
      return;
    }

    // ルール検証を通過しても、画面側の配達対象外エラーがある場合は検索しない。
    if (props.errors.zip) {
      setZipFieldErrors([props.errors.zip]);
      return;
    }

    // クリック時点で郵便番号エラーが表示中なら検索を実行しない。
    if (zipFieldErrorMessage.value) {
      return;
    }
  }
  if (currentMode === 1) {
    const result = await validatePrefField();
    if (!result.valid) {
      return;
    }

    if (props.errors.pref) {
      setPrefFieldErrors([props.errors.pref]);
      return;
    }
  }

  emit("search");
};

/** カナ行表示ラベル定義を表す定数。 */
const kanaGroups: Array<{ key: KanaGroupKey; label: string }> = [
  { key: "line_a", label: "あ行" },
  { key: "line_ka", label: "か行" },
  { key: "line_sa", label: "さ行" },
  { key: "line_ta", label: "た行" },
  { key: "line_na", label: "な行" },
  { key: "line_ha", label: "は行" },
  { key: "line_ma", label: "ま行" },
  { key: "line_ya", label: "や行" },
  { key: "line_ra", label: "ら行" },
  { key: "line_wa", label: "わ行" },
  { key: "line_other", label: "その他" },
];

/** 都道府県候補を表示用に正規化する。 */
const normalizedPrefListItems = computed<string[]>(() => {
  return props.prefListItems.filter(
    (value): value is string => typeof value === "string" && value.length > 0
  );
});

/** 都道府県選択値を文字列へ正規化して通知する。 */
const onPrefModelUpdate = (
  value: string | { value?: string; title?: string } | null | undefined
): void => {
  if (typeof value === "string") {
    setPrefFieldErrors([]);
    setPrefFieldValue(value, false);
    emit("update:pref", value);
    return;
  }

  if (value && typeof value === "object") {
    const normalized = value.value ?? value.title ?? "";
    setPrefFieldErrors([]);
    setPrefFieldValue(normalized, false);
    emit("update:pref", normalized);
    return;
  }

  setPrefFieldErrors([]);
  setPrefFieldValue("", false);
  emit("update:pref", "");
};

/** 都道府県入力のフォーカスアウト時に検証する。 */
const onPrefBlur = async (): Promise<void> => {
  await validatePrefField();
  emit("prefInput");
};

/** その他丁目かどうかを判定する。 */
const isOtherBlock = (name: string | null | undefined): boolean => {
  const otherLabel = props.wordList.lbl_other_block_disp_name || "その他";
  const normalizedName = (name ?? "").trim();
  if (!normalizedName) {
    return true;
  }
  return normalizedName === "その他" || normalizedName === otherLabel;
};

/** 丁目ラベルを表示用に正規化する。 */
const normalizeBlockName = (name: string | null | undefined): string => {
  const normalizedName = (name ?? "").trim();
  if (normalizedName) {
    return normalizedName;
  }
  return props.wordList.lbl_other_block_disp_name || "その他";
};

/** 表示用に正規化した丁目候補を取得する。 */
const normalizedBlockItems = computed(() => {
  return props.blockList.map((block) => ({
    ...block,
    name: normalizeBlockName(block.name),
  }));
});

/** 通常表示する丁目候補を取得する。 */
const normalBlockItems = computed(() => {
  return normalizedBlockItems.value.filter(
    (block) => !isOtherBlock(block.name) || (isOtherBlock(block.name) && !block.enabled)
  );
});

/** 末尾に分離表示する「その他」丁目候補を取得する。 */
const enabledOtherBlockItems = computed(() => {
  return normalizedBlockItems.value.filter((block) => isOtherBlock(block.name) && block.enabled);
});

/** 丁目選択画面の案内文言表示可否を判定する。 */
const showOtherBlockGuideMessage = computed(() => {
  return enabledOtherBlockItems.value.length > 0;
});

/** 丁目選択ステージかどうかを判定する。 */
const isBlockStage = computed(() => {
  // 市区町村選択後で、かつ、丁目候補が存在する場合を丁目選択ステージとする。
  return normalizedBlockItems.value.length > 0;
});

/** 丁目選択画面の案内文言全体表示可否を判定する。 */
const showBlockGuideMessage = computed(() => {
  return isBlockStage.value && (!!props.areaNotSelectMessage || showOtherBlockGuideMessage.value);
});

/** カナ行ごとの候補を取得する。 */
const getKanaItems = (list: KanaStepA | null, key: KanaGroupKey): string[] => {
  if (!list) {
    return [];
  }

  return (list[key] ?? []).filter((value): value is string => typeof value === "string" && value.length > 0);
};
</script>

<style scoped>
.address-input {
  width: 80%;
  max-width: 560px;
  margin: 0 auto;
}
</style>
